import { useCallback, useState } from 'react';
import { CardContent } from '@/shared/components/ui/card';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type {
  LoginInitialRequestDto,
  LoginMfaRequiredResponseDto,
} from '@/features/auth-login';
import { sendOtpCode, useMFA, useMfaForm } from '@/features/auth-login';
import { AuthCard } from '@/shared/components';
import {
  MfaAppDescription,
  MfaConfirmationScreen,
  type MFAIdentityType,
  type MfaStateData,
} from '@/entities/mfa-identity';
import { useIdleTimer } from 'react-idle-timer';
import { useSessionIdle } from '@/shared/lib';

type Props = {
  className?: string;
};

export type LoginMfaFormLocationState = {
  loginArgs: LoginInitialRequestDto;
  mfaType: MFAIdentityType;
  identityTypes: LoginMfaRequiredResponseDto['data']['identityTypes'];
  mfaState: string;
  mfaData?: MfaStateData;
};

const LoginMfaForm = ({ className }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { twoFAstateTimeout } = useSessionIdle();

  const { loginArgs, mfaType, mfaState, identityTypes, mfaData } =
    location.state as LoginMfaFormLocationState;

  const currentMfaData = mfaData?.[mfaType];

  const [maskedContact, setMaskedContact] = useState<string>(
    currentMfaData?.maskedContact ?? ''
  );
  const [resendUntil, setResendUntil] = useState<number | undefined>(
    currentMfaData?.resendUntil ?? 0
  );
  const [shouldSendOtpOnMount] = useState(
    () =>
      (currentMfaData?.resendUntil ?? 0) - Date.now() <= 0 &&
      mfaType?.toLowerCase() != 'googleauthenticator'
  );

  const { form, resetCode } = useMfaForm(loginArgs, mfaType, mfaState);

  const onIdle = useCallback(async () => {
    navigate('/login');
  }, [twoFAstateTimeout]);

  useIdleTimer({
    onIdle,
    timeout: twoFAstateTimeout,
    throttle: 500,
    disabled: import.meta.env.DEV,
  });

  const { runLoginWithMfaCode, mfaError, isMfaLoading } = useMFA(resetCode);

  if (
    !location.state ||
    !location.state.loginArgs ||
    !location.state.mfaType ||
    !location.state.mfaState ||
    !location.state.identityTypes
  ) {
    return <Navigate to="/login" />;
  }

  const sendOtpCodeMutationFn = async () =>
    await sendOtpCode({ state: mfaState, identityType: mfaType });

  const remaining = (resendUntil ?? 0) - Date.now();

  const shouldDisplayOtpText =
    mfaType?.toLowerCase() != 'googleauthenticator' || !!maskedContact;

  return (
    <AuthCard
      className={className}
      title={t('authPages.identityConfirmationForm.title')}
      description={
        shouldDisplayOtpText ? (
          `${t('authPages.identityConfirmationForm.description')} ${maskedContact}`
        ) : (
          <MfaAppDescription />
        )
      }
      backButton={{
        link: identityTypes.length > 1 ? '/login/mfa-methods' : '/login',
        state: {
          ...location.state,
          mfaData: {
            ...location.state.mfaData,
            [mfaType]: {
              resendUntil,
              maskedContact,
            },
          },
        },
      }}>
      <CardContent className="p-0">
        <MfaConfirmationScreen
          form={form}
          identityType={mfaType}
          shouldSendOtpOnMount={shouldSendOtpOnMount}
          resendIn={remaining}
          mfaCodeFieldName="twoFA.code"
          sendOtpCodeMutationFn={sendOtpCodeMutationFn}
          setMaskedContact={setMaskedContact}
          setResendUntil={setResendUntil}
          onSubmit={() => runLoginWithMfaCode(form.getValues())}
          isSubmitLoading={isMfaLoading}
          submitError={mfaError || undefined}
        />
      </CardContent>
    </AuthCard>
  );
};

export { LoginMfaForm };
